import { useEffect, useState } from "react";
import { ArrowUpRight, Home, Maximize2, Bed, Car, Archive, Accessibility, FileDown, TrendingUp } from "lucide-react";
import { APARTMENTS } from "@/data/apartments";
import { ApartmentDetailDialog } from "./ApartmentDetailDialog";
import { AvailabilityBadge } from "./AvailabilityBadge";
import api, { API } from "@/lib/api";
import { useT } from "@/i18n/LanguageProvider";

export const Apartments = ({ focusApartmentId = null }) => {
    const t = useT();
    const [selected, setSelected] = useState(null);
    const [statusMap, setStatusMap] = useState({});

    const refreshStatus = async () => {
        try {
            const { data } = await api.get("/apartments");
            const map = {};
            (data || []).forEach((a) => { map[a.id] = a.status; });
            setStatusMap(map);
        } catch (e) {
            // silent
        }
    };

    useEffect(() => { refreshStatus(); }, []);

    // When arriving via /wohnung/:slug routes, open the apartment detail
    // dialog directly so the user immediately sees the apartment they came for.
    // (Auto-scroll would conflict with our IntersectionObserver-based Reveal
    // animations and feel sluggish.)
    useEffect(() => {
        if (!focusApartmentId) return;
        const apt = APARTMENTS.find((a) => a.id === focusApartmentId);
        if (!apt) return;
        const t = setTimeout(() => setSelected(apt), 600);
        return () => clearTimeout(t);
    }, [focusApartmentId]);

    // Sticky quick-pick bar (ApartmentStickyBar) opens the detail dialog via event.
    useEffect(() => {
        const onOpen = (e) => {
            const apt = APARTMENTS.find((a) => a.id === e.detail?.apartmentId);
            if (apt) setSelected(apt);
        };
        window.addEventListener("g21:open-apartment", onOpen);
        return () => window.removeEventListener("g21:open-apartment", onOpen);
    }, []);

    const handleExpose = (e, apartmentId) => {
        e.stopPropagation();
        const url = `${API}/apartments/${apartmentId}/exposee.pdf`;
        window.open(url, "_blank");
    };

    // Jump to the rental-yield calculator and preselect the apartment via a
    // CustomEvent. The forecast component listens and updates its selected tab.
    const handleYield = (e, apartmentId) => {
        e.stopPropagation();
        window.dispatchEvent(
            new CustomEvent("g21:select-rental-apartment", {
                detail: { apartmentId },
            }),
        );
        const el = document.getElementById("mietrendite");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section id="wohnungen" className="bg-white py-24 sm:py-32" data-testid="apartments-section">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16">
                    <div className="lg:col-span-7">
                        <span className="overline">{t({ de: "02 / 12 — Wohnungs\u00ADübersicht", en: "02 / 12 — Apartment overview" })}</span>
                        <h2 className="h-section mt-4 text-balance" data-testid="apartments-title">
                            {t({ de: "Vier Wohnungen,", en: "Four apartments," })}
                            <span className="italic text-brand-500"> {t({ de: "jede ein Original.", en: "each an original." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="body-md">
                            {t({
                                de: "Klicken Sie auf eine Wohnung, um Details, Grundriss und das Anfrage-Formular zu öffnen. Stellplatz-Carport je 29.000 €. Käufe direkt vom Bauträger.",
                                en: "Click on an apartment to view details, floor plan and the inquiry form. Carport parking at €29,000 each. Purchase directly from the developer.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
                    {APARTMENTS.map((apt, i) => {
                        const status = statusMap[apt.id] || "verfuegbar";
                        const sold = status === "verkauft";
                        return (
                            <article
                                key={apt.id}
                                id={apt.id}
                                className={`group relative bg-cream border border-stone-200 rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
                                    i % 2 === 1 ? "md:mt-12" : ""
                                } ${sold ? "opacity-90" : ""}`}
                                data-testid={`apartment-card-${apt.id}`}
                            >
                                <div className="frame-img aspect-[4/3] w-full overflow-hidden bg-stone-100 relative">
                                    <img
                                        src={apt.image}
                                        alt={apt.name}
                                        className={`w-full h-full object-cover ${sold ? "grayscale-[40%]" : ""}`}
                                    />
                                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur-sm">
                                        <span className="font-body text-xs uppercase tracking-[0.2em] text-stone-700">
                                            {apt.position}
                                        </span>
                                    </div>
                                    <div className="absolute top-4 right-4">
                                        <AvailabilityBadge status={status} size="lg" className="bg-white/95 backdrop-blur-sm" />
                                    </div>
                                </div>

                                <div className="p-7 lg:p-9">
                                    <div className="flex items-start justify-between gap-6 mb-3">
                                        <div>
                                            <h3 className="font-heading text-2xl lg:text-3xl text-ink leading-tight">
                                                {apt.name}
                                            </h3>
                                            <p className="font-body text-sm text-stone-500 mt-1">
                                                {apt.rooms} {t({ de: "Zimmer", en: "rooms" })} · {apt.size} {apt.sizeUnit} · {t({ de: "mit", en: "with" })}{" "}
                                                {apt.outdoor}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="overline text-stone-400">{t({ de: "ab", en: "from" })}</div>
                                            <div className="font-heading text-2xl text-brand-600">
                                                {apt.priceLabel}
                                            </div>
                                        </div>
                                    </div>

                                    <span className="inline-block mb-5 px-2.5 py-1 bg-clay-50 text-brand-700 font-body text-[11px] uppercase tracking-[0.18em]">
                                        {apt.tagline}
                                    </span>

                                    <p className="font-body text-sm text-stone-600 leading-relaxed mb-6">
                                        {apt.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mb-7">
                                        <Pill icon={Maximize2} label={`${apt.size} m²`} />
                                        <Pill icon={Bed} label={`${apt.rooms} ${t({ de: "Zimmer", en: "rooms" })}`} />
                                        <Pill icon={Car} label={t({ de: "Carport", en: "Carport" })} />
                                        <Pill icon={Archive} label={t({ de: "Keller", en: "Storage" })} />
                                        {apt.accessible && (
                                            <Pill icon={Accessibility} label={t({ de: "Barrierefrei", en: "Barrier-free" })} />
                                        )}
                                    </div>

                                    <div className="flex flex-wrap items-stretch gap-3 pt-4 border-t border-stone-200">
                                        <button
                                            onClick={(e) => handleExpose(e, apt.id)}
                                            className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 hover:border-brand-500 hover:bg-cream font-body text-sm text-stone-700 transition-colors"
                                            data-testid={`apartment-expose-${apt.id}`}
                                        >
                                            <FileDown size={14} />
                                            {t({ de: "Exposé PDF", en: "Brochure PDF" })}
                                        </button>
                                        <button
                                            onClick={(e) => handleYield(e, apt.id)}
                                            className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 hover:border-brand-500 hover:bg-cream font-body text-sm text-stone-700 transition-colors"
                                            data-testid={`apartment-yield-${apt.id}`}
                                            title={t({ de: "Rendite für diese Wohnung ausrechnen", en: "Calculate yield for this apartment" })}
                                        >
                                            <TrendingUp size={14} />
                                            {t({ de: "Rendite ausrechnen", en: "Calculate yield" })}
                                        </button>
                                        <button
                                            onClick={() => setSelected(apt)}
                                            className="group/btn inline-flex items-center justify-between gap-3 flex-1 min-w-[220px] px-4 py-2 bg-ink text-white hover:bg-brand-500 transition-colors"
                                            data-testid={`apartment-cta-${apt.id}`}
                                        >
                                            <span className="font-body text-sm font-medium tracking-wide">
                                                {sold
                                                    ? t({ de: "Details ansehen", en: "View details" })
                                                    : t({ de: "Details & Anfrage stellen", en: "Details & inquire" })}
                                            </span>
                                            <ArrowUpRight
                                                size={16}
                                                className="transition-transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>

                <div className="mt-12 px-6 py-5 bg-clay-50 border-l-4 border-brand-500 flex flex-wrap items-center gap-3">
                    <Home size={18} className="text-brand-600" />
                    <span className="font-body text-sm text-stone-700">
                        {t({
                            de: <>Hinweis: Alle Preise zzgl. PKW-Carport im Hintergebäude (29.000 € pro Stellplatz). Fahrradständer im Innenhof inklusive. Kaufnebenkosten ca. <strong>7,5 %</strong> (6 % Grunderwerbsteuer Hessen + ca. 1,5 % Notar &amp; Grundbuch · maklerfrei).</>,
                            en: <>Note: prices exclude the rear carport (€29,000 per spot). Bike racks in the courtyard are included. Closing costs ≈ <strong>7.5 %</strong> (6 % real-estate transfer tax in Hesse + approx. 1.5 % notary &amp; land register · brokerage-free).</>,
                        })}
                    </span>
                </div>
            </div>

            <ApartmentDetailDialog
                apartment={selected}
                status={selected ? statusMap[selected.id] : null}
                open={Boolean(selected)}
                onOpenChange={(o) => !o && setSelected(null)}
            />
        </section>
    );
};

const Pill = ({ icon: Icon, label }) => (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-stone-200 text-stone-700 text-xs font-body">
        <Icon size={13} strokeWidth={1.5} />
        {label}
    </span>
);
